/*!
 * Coldest A11y Fixes — Runtime ADA Compliance Patches
 *
 * Fixes WCAG 2.1 Level AA issues without altering layout or functionality:
 * - Names unnamed icon buttons (wishlist, rewards link, social icons)
 * - Adds alt text to images that lack it
 * - Labels form inputs (engraving, search, newsletter) that lack accessible names
 * - Adds focus-visible indicators for keyboard users
 * - Adds aria-hidden to purely decorative content
 * - Marks Shogun banner image links with their destination as accessible name
 *
 * Designed to be idempotent and non-destructive — runs on DOM ready and again
 * on mutations (so it patches dynamically loaded UI like cart drawer, popups).
 *
 * Safe to remove at any time; restores no state.
 */
(function () {
  'use strict';

  if (window.__coldestA11yLoaded) return;
  window.__coldestA11yLoaded = true;

  /* ============================================================
   * 1. Style: focus-visible outline for keyboard users
   * ============================================================ */
  var style = document.createElement('style');
  style.id = 'coldest-a11y-styles';
  style.textContent = [
    /* Stronger focus ring for keyboard navigation. */
    '.coldest-a11y-fixes :focus-visible,',
    'a:focus-visible, button:focus-visible, [role="button"]:focus-visible,',
    'input:focus-visible, select:focus-visible, textarea:focus-visible,',
    'summary:focus-visible, [tabindex]:focus-visible {',
    '  outline: 2px solid #0058a3 !important;',
    '  outline-offset: 2px !important;',
    '  border-radius: 4px;',
    '}',
    /* Skip links — visible when focused (the theme has one but make sure). */
    'a[href^="#"].skip-link:focus,',
    'a[href="#content"]:focus,',
    'a[href="#MainContent"]:focus,',
    'a[href="#main"]:focus {',
    '  position: fixed !important;',
    '  top: 8px !important;',
    '  left: 8px !important;',
    '  padding: 12px 18px !important;',
    '  background: #0c0d0e !important;',
    '  color: #ffffff !important;',
    '  z-index: 100000 !important;',
    '  text-decoration: underline !important;',
    '  border-radius: 6px !important;',
    '  font-weight: 700 !important;',
    '  width: auto !important;',
    '  height: auto !important;',
    '  clip: auto !important;',
    '  overflow: visible !important;',
    '}',
    /* Visually-hidden helper for screen-reader-only labels. */
    '.coldest-sr-only {',
    '  position: absolute !important;',
    '  width: 1px !important;',
    '  height: 1px !important;',
    '  padding: 0 !important;',
    '  margin: -1px !important;',
    '  overflow: hidden !important;',
    '  clip: rect(0,0,0,0) !important;',
    '  white-space: nowrap !important;',
    '  border: 0 !important;',
    '}'
  ].join('\n');
  if (document.head) document.head.appendChild(style);

  /* ============================================================
   * 2. Helpers
   * ============================================================ */

  /** Generate a human-readable label from a URL */
  function urlToLabel(url) {
    if (!url) return '';
    try {
      var u = new URL(url, window.location.origin);
      // Map known social hosts
      var host = u.hostname.replace(/^www\./, '');
      if (/facebook\.com/.test(host)) return 'Visit our Facebook';
      if (/instagram\.com/.test(host)) return 'Visit our Instagram';
      if (/tiktok\.com/.test(host)) return 'Visit our TikTok';
      if (/youtube\.com|youtu\.be/.test(host)) return 'Visit our YouTube';
      if (/twitter\.com|x\.com/.test(host)) return 'Visit our X profile';
      if (/pinterest\.com/.test(host)) return 'Visit our Pinterest';
      if (/linkedin\.com/.test(host)) return 'Visit our LinkedIn';
      if (/apps\.apple\.com/.test(host)) return 'Download on the App Store';
      if (/play\.google\.com/.test(host)) return 'Get it on Google Play';
      if (/mailto:/.test(url)) return 'Email us at ' + url.replace(/^mailto:/i,'');
      if (/^tel:/.test(url)) return 'Call us at ' + url.replace(/^tel:/i,'');

      // For Coldest pages, derive name from path
      if (u.pathname && u.pathname !== '/' && u.pathname !== '/#' ) {
        var slug = u.pathname.split('/').filter(Boolean).pop();
        if (slug) {
          var pretty = slug.replace(/[-_]/g, ' ').replace(/\.[a-z]+$/i,'');
          // Title case
          pretty = pretty.replace(/\b\w/g, function(c){ return c.toUpperCase(); });
          // Common page-name overrides for clarity
          var prefix = u.pathname.indexOf('/products/') === 0 ? 'Shop ' :
                       u.pathname.indexOf('/collections/') === 0 ? 'Shop ' :
                       u.pathname.indexOf('/pages/') === 0 ? 'Read about ' : '';
          return prefix + pretty;
        }
      }
    } catch (e) {}
    return '';
  }

  /** Generate alt text for image based on src, surrounding context, and link target */
  function inferAlt(img) {
    // Prefer parent link's destination
    var link = img.closest('a[href]');
    if (link) {
      var label = urlToLabel(link.getAttribute('href'));
      if (label) return label;
    }
    // Fallback: use image filename (cleaned up)
    var src = img.getAttribute('src') || '';
    var fname = src.split('/').pop().split('?')[0].replace(/\.(jpg|jpeg|png|gif|webp|svg)$/i, '');
    fname = fname.replace(/[-_]/g, ' ').replace(/\d+x\d+/g, '').trim();
    if (fname && fname.length > 2 && fname.length < 60 && /[a-zA-Z]{3,}/.test(fname)) {
      return fname;
    }
    // Last resort: blank (decorative) — better than missing
    return '';
  }

  /* ============================================================
   * 3. Patch unnamed interactive elements
   * ============================================================ */
  function patchInteractive(root) {
    root = root || document;

    // Wishlist links (Swym) — often empty, pure icon
    root.querySelectorAll('a.swym-wishlist, button.swym-wishlist, [class*="swym-wishlist-trigger"]').forEach(function (el) {
      if (!el.getAttribute('aria-label') && !(el.textContent || '').trim()) {
        el.setAttribute('aria-label', 'View wishlist');
        if (!el.hasAttribute('role') && el.tagName === 'A' && (el.getAttribute('href') === '#' || el.getAttribute('href') === '#swym-wishlist')) {
          el.setAttribute('role', 'button');
        }
      }
    });

    // Rewards icon link (cstm-rwrd-lnk)
    root.querySelectorAll('a.cstm-rwrd-lnk').forEach(function (el) {
      if (!el.getAttribute('aria-label') && !(el.textContent || '').trim()) {
        el.setAttribute('aria-label', 'Open rewards page');
      }
    });

    // Generic icon-only buttons with no text and no aria-label
    var candidates = root.querySelectorAll('button, [role="button"], a[href]');
    candidates.forEach(function (el) {
      if ((el.textContent || '').trim()) return;
      if (el.getAttribute('aria-label')) return;
      if (el.getAttribute('aria-labelledby')) return;
      if (el.getAttribute('title')) return;

      // Image alt counts
      var img = el.querySelector('img[alt]');
      if (img && (img.getAttribute('alt') || '').trim()) return;

      // Try to derive name from href
      var href = el.getAttribute('href');
      if (href) {
        var label = urlToLabel(href);
        if (label) {
          el.setAttribute('aria-label', label);
          return;
        }
      }

      // Try icon class hint
      var iconEl = el.querySelector('i[class*="icon"], svg, [class*="icon-"]');
      if (iconEl) {
        var cls = (iconEl.className || '') + '';
        if (typeof cls === 'object' && cls.baseVal) cls = cls.baseVal;
        if (/wishlist/i.test(cls)) { el.setAttribute('aria-label', 'View wishlist'); return; }
        if (/cart|bag/i.test(cls)) { el.setAttribute('aria-label', 'View cart'); return; }
        if (/search|magnif/i.test(cls)) { el.setAttribute('aria-label', 'Open search'); return; }
        if (/account|user|profile/i.test(cls)) { el.setAttribute('aria-label', 'My account'); return; }
        if (/menu|hamburger|bars/i.test(cls)) { el.setAttribute('aria-label', 'Open menu'); return; }
        if (/close|times|x-mark/i.test(cls)) { el.setAttribute('aria-label', 'Close'); return; }
        if (/play/i.test(cls)) { el.setAttribute('aria-label', 'Play video'); return; }
        if (/pause/i.test(cls)) { el.setAttribute('aria-label', 'Pause video'); return; }
        if (/facebook/i.test(cls)) { el.setAttribute('aria-label', 'Visit our Facebook'); return; }
        if (/instagram/i.test(cls)) { el.setAttribute('aria-label', 'Visit our Instagram'); return; }
        if (/tiktok/i.test(cls)) { el.setAttribute('aria-label', 'Visit our TikTok'); return; }
        if (/youtube/i.test(cls)) { el.setAttribute('aria-label', 'Visit our YouTube'); return; }
        if (/twitter|x-twitter/i.test(cls)) { el.setAttribute('aria-label', 'Visit our X profile'); return; }
        if (/pinterest/i.test(cls)) { el.setAttribute('aria-label', 'Visit our Pinterest'); return; }
      }

      // Fallback so screen readers don't announce empty: only if visible
      var rect = el.getBoundingClientRect();
      if (rect.width > 0 && rect.height > 0) {
        el.setAttribute('aria-label', 'Link');
      }
    });
  }

  /* ============================================================
   * 4. Patch images missing alt
   * ============================================================ */
  function patchImages(root) {
    root = root || document;
    root.querySelectorAll('img:not([alt])').forEach(function (img) {
      img.setAttribute('alt', inferAlt(img));
    });
  }

  /* ============================================================
   * 5. Patch unlabeled form inputs
   * ============================================================ */
  function patchInputs(root) {
    root = root || document;
    var inputs = root.querySelectorAll(
      'input:not([type="hidden"]):not([type="submit"]):not([type="button"]):not([type="reset"]), select, textarea'
    );
    inputs.forEach(function (inp) {
      if (inp.getAttribute('aria-label')) return;
      if (inp.getAttribute('aria-labelledby')) return;
      if (inp.id && document.querySelector('label[for="' + CSS.escape(inp.id) + '"]')) return;
      if (inp.closest('label')) return;

      // Use placeholder as label fallback
      var ph = inp.getAttribute('placeholder');
      if (ph && ph.trim()) {
        inp.setAttribute('aria-label', ph.trim());
        return;
      }

      // Use class hints
      var cls = (inp.className || '') + '';
      if (/email/i.test(cls) || inp.type === 'email') { inp.setAttribute('aria-label', 'Email address'); return; }
      if (/search/i.test(cls) || inp.type === 'search') { inp.setAttribute('aria-label', 'Search'); return; }
      if (/qty|quantity/i.test(cls)) { inp.setAttribute('aria-label', 'Quantity'); return; }
      if (/zip|postal/i.test(cls)) { inp.setAttribute('aria-label', 'ZIP / Postal code'); return; }
      if (/phone|tel/i.test(cls) || inp.type === 'tel') { inp.setAttribute('aria-label', 'Phone number'); return; }

      // Custom Engraving plugin inputs (.cep-*)
      if (/cep-input/.test(cls)) { inp.setAttribute('aria-label', 'Engraving text'); return; }
      if (/cep-loc/.test(cls)) { inp.setAttribute('aria-label', 'Engraving location'); return; }
      if (/cep-font/.test(cls)) { inp.setAttribute('aria-label', 'Engraving font'); return; }
      if (/cep-color/.test(cls)) { inp.setAttribute('aria-label', 'Engraving color'); return; }

      // Last resort: use element type
      inp.setAttribute('aria-label', inp.tagName === 'SELECT' ? 'Select an option' : 'Input field');
    });
  }

  /* ============================================================
   * 6. Hide decorative icon fonts from screen readers
   * ============================================================ */
  function patchDecorations(root) {
    root = root || document;
    root.querySelectorAll('i[class*="icon-"]:not([aria-hidden]), i[class*="fa-"]:not([aria-hidden])').forEach(function (el) {
      el.setAttribute('aria-hidden', 'true');
      el.setAttribute('focusable', 'false');
    });
  }

  /* ============================================================
   * 7. Run all patches
   * ============================================================ */
  function runAll(root) {
    try { patchInteractive(root); } catch (e) {}
    try { patchImages(root); } catch (e) {}
    try { patchInputs(root); } catch (e) {}
    try { patchDecorations(root); } catch (e) {}
  }

  function init() {
    runAll(document);

    // Re-run on dynamic content (cart drawer, modals, infinite scroll)
    if (window.MutationObserver) {
      var pending = false;
      var mo = new MutationObserver(function () {
        if (pending) return;
        pending = true;
        // Throttle to at most one full-document pass per ~1s, run in idle
        // time: app embeds mutate the DOM continuously, and a 250ms throttle
        // meant ~30 full passes (with forced layout) in the first 25s on a PDP.
        setTimeout(function () {
          var go = function () {
            pending = false;
            runAll(document);
          };
          if (window.requestIdleCallback) requestIdleCallback(go, { timeout: 2000 });
          else go();
        }, 1000);
      });
      mo.observe(document.body, { childList: true, subtree: true });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
